import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCallerState } from "./callerState";

export type OpenProgram = { slug: string; state?: string };
type Ctx = { current: OpenProgram | null; open: (slug: string, state?: string) => void; close: () => void };
const PanelCtx = createContext<Ctx>({ current: null, open: () => {}, close: () => {} });

/**
 * The one program side panel of the screen. The search box, the results page and problem pages open programs here,
 * so the route never changes; the layout docks the panel beside the page.
 */
export function ProgramPanelProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<OpenProgram | null>(null);
  const open = useCallback((slug: string, state?: string) => setCurrent({ slug, state }), []);
  const close = useCallback(() => setCurrent(null), []);

  // The open program follows the caller's state: when the top bar changes for the next caller, the panel shows that
  // state's contacts and apply link, never the last caller's. (The panel's own select still changes only the panel.)
  const caller = useCallerState();
  const seenCaller = useRef(caller.state);
  useEffect(() => {
    if (caller.state === seenCaller.current) return;
    seenCaller.current = caller.state;
    setCurrent((c) => (c && c.state !== caller.state ? { ...c, state: caller.state } : c));
  }, [caller.state]);

  // A session that ends closes the panel, as the Sign out button does.
  useEffect(() => {
    window.addEventListener("afhh:signed-out", close);
    return () => window.removeEventListener("afhh:signed-out", close);
  }, [close]);

  const value = useMemo(() => ({ current, open, close }), [current, open, close]);
  return <PanelCtx.Provider value={value}>{children}</PanelCtx.Provider>;
}

export const useProgramPanel = (): Ctx => useContext(PanelCtx);
